import moment from 'moment-timezone'
import { Typography } from 'antd'

import { isReservationTransaction, V3Transaction } from 'models/v3-transaction'

import { RefundIcon } from 'assets/svg/refundIcon'
import UserName from 'components/title/TitleAndSubtitle'
import { V3TransactionStateBadge, PaymentStateBadge } from 'components/transactions/transaction-state-badge'

import { useGeneralTranslation } from 'hooks/translation/useGeneralTranslation'
import { useUserTranslation } from 'hooks/translation/useUserTranslation'
import { useListingBasicTrans } from 'hooks/translation/useListingBasicTrans'
import { useSelector } from 'hooks/useSelector'
import ReservationBadge from '../common/ReservationBadge'

import { formatDurationWithTwoDigits } from 'helpers/duration'
import { formatEnergy, formatPrice } from 'helpers/number-format'
import { theme } from 'theme'

export const V3TransactionsColumns = (
  showPanel: boolean,
  onChargingDurationClick?: (transaction: V3Transaction) => void,
) => {
  const { chargingDurationText, tenantText } = useGeneralTranslation()
  const { listingTitleText } = useListingBasicTrans()
  const { userText } = useUserTranslation()
  const { siteLabelCleaner } = useSelector()

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      align: 'left' as const,
      render: (id: number, transaction: V3Transaction) => {
        const isReservation = isReservationTransaction(transaction)
        return (
          <div>
            <div>{id}</div>
            {isReservation ? (
              <div style={{ marginTop: '4px' }}>
                <ReservationBadge />
              </div>
            ) : null}
          </div>
        )
      },
    },
    {
      title: userText,
      key: 'user_name',
      align: 'left' as const,
      render: (transaction: V3Transaction) => <UserName transaction={transaction as any} />,
    },
    {
      title: tenantText,
      key: 'tenant',
      align: 'left' as const,
      render: ({ tenant: { combineName } }: V3Transaction) => (
        <Typography.Text style={{ width: 250 }} ellipsis={{ tooltip: siteLabelCleaner(combineName) }}>
          {siteLabelCleaner(combineName)}
        </Typography.Text>
      ),
    },
    { title: listingTitleText, key: 'listing', align: 'left' as const, render: ({ listing: { title } }: V3Transaction) => title },
    {
      title: 'Connected/Disconnected',
      key: 'connectedDisconnected',
      align: 'left' as const,
      render: ({ startAt, endAt, timezone }: V3Transaction) => {
        const lineStyle = { color: theme.colors.grey7, fontSize: '12px' }
        const tzStyle = { color: theme.colors.grey7, fontSize: '9px' }
        const fmt = (d: string) => {
          const m = timezone ? moment(d).tz(timezone) : moment(d)
          return { dateTime: `${m.format('YYYY/MM/DD')} ${m.format('HH:mm:ss')}`, tz: m.format('z') }
        }
        const start = fmt(startAt)
        const end = endAt ? fmt(endAt) : null
        return (
          <div>
            <div style={lineStyle}>
              {start.dateTime} <span style={tzStyle}>{start.tz}</span>
            </div>
            <div style={lineStyle}>
              {end ? <>{end.dateTime} <span style={tzStyle}>{end.tz}</span></> : '-'}
            </div>
          </div>
        )
      },
    },
    {
      title: chargingDurationText,
      key: 'current_charging_duration',
      align: 'left' as const,
      render: (transaction: V3Transaction) => {
        if (transaction.currentChargingDuration === null || transaction.currentChargingDuration === undefined) {
          return '-'
        }
        const duration = formatDurationWithTwoDigits(transaction.currentChargingDuration)
        const canOpenGraph = (transaction.currentChargingDuration ?? 0) > 0
        if (onChargingDurationClick && canOpenGraph) {
          return (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation()
                onChargingDurationClick(transaction)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  onChargingDurationClick(transaction)
                }
              }}
              style={{ color: 'black', textDecoration: 'underline', cursor: 'pointer' }}
            >
              {duration}
            </span>
          )
        }
        return duration
      },
    },
  ]

  const stateCellProps = { style: { verticalAlign: 'middle' } }
  columns.push({
    title: 'Transaction',
    key: 'currentState',
    align: 'left' as const,
    onCell: () => stateCellProps,
    render: (trx: V3Transaction) => {
      const energyDisplay =
        trx.energy === null || trx.energy === undefined ? '-' : `${formatEnergy(Math.max(0, trx.energy) / 1000)} kWh`
      return (
        <div>
          <div style={{ fontSize: '12px' }}>{energyDisplay}</div>
          <div style={{ marginTop: '4px' }}><V3TransactionStateBadge transaction={trx} /></div>
        </div>
      )
    },
  } as any)
  columns.push({
    title: 'Total',
    key: 'total',
    align: 'left' as const,
    render: ({ financial: { total, currency } }: V3Transaction) => (
      <div>
        <div style={{ fontWeight: 700 }}>{formatPrice(total / 100)}</div>
        {currency ? <div className="opacity-06 paragraph-04-light">{currency.toUpperCase()}</div> : null}
      </div>
    ),
  })
  columns.push({
    title: 'Payment',
    key: 'paymentState',
    align: 'left' as const,
    onCell: () => stateCellProps,
    render: (trx: V3Transaction) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <PaymentStateBadge payment={trx.payment} />
        {trx.refund && (
          <span style={{ display: 'inline-flex', lineHeight: 0 }}>{RefundIcon}</span>
        )}
      </div>
    ),
  } as any)

  return columns
}

